import React from 'react';
import { Link } from 'react-router-dom';
import { Icon } from '@iconify/react';
import { openExternalUrl } from '@/utils/openExternalUrl';

// Types
export interface MabarClientEvent {
  slug: string;
  title: string;
  date: string;
  time: string;
  venue: string;
  sportType: string;
  sportSlug: string;
  sportIcon?: string;
  currentParticipants: number;
  maxParticipants: number;
  isHost: boolean;
  isPrivate: boolean;
  linkGroup?: string;
  status: string;
  price?: number | 'free';
  isParticipant: boolean;
  participantStatus?: string;
  autoConfirm?: boolean;
  /** Raw event_date from API (YYYY-MM-DD) for history card formatting */
  eventDate?: string;
  paymentStatus?: string;
  invoiceUrl?: string;
  paymentMethod?: string;
  orderNumber?: string;
  hostName?: string;
  hostAvatar?: string;
  mapLink?: string;
}

interface MabarClientCardProps {
  event: MabarClientEvent;
  onViewDetail?: (event: MabarClientEvent) => void;
  onViewDashboard?: (event: MabarClientEvent) => void;
  onEdit?: (event: MabarClientEvent) => void;
  onShare?: (event: MabarClientEvent) => void;
  onCancel?: (event: MabarClientEvent) => void;
  onLeave?: (event: MabarClientEvent) => void;
  onJoin?: (event: MabarClientEvent) => void;
  onViewPadel?: (event: MabarClientEvent) => void;
  showPadelCta?: boolean;
  onViewBadminton?: (event: MabarClientEvent) => void;
  showBadmintonCta?: boolean;
  onPay?: (event: MabarClientEvent) => void;
}

const MabarClientCard: React.FC<MabarClientCardProps> = ({
  event,
  onViewDetail,
  // onViewDashboard,
  // onEdit,
  // onShare,
  // onCancel,
  onLeave,
  onJoin,
  onViewPadel,
  showPadelCta,
  onViewBadminton,
  showBadmintonCta,
  onPay,
}) => {

  const participantPercentage = Math.round(
    (event.currentParticipants / event.maxParticipants) * 100
  );

  const isUnpaidQris =
    event.isParticipant &&
    (event.participantStatus === 'unpaid' ||
      (event.paymentStatus?.toLowerCase() === 'unpaid' &&
        event.paymentMethod?.toLowerCase() === 'qris'));

  const getSportIcon = (sportType: string): string => {
    const icons: Record<string, string> = {
      futsal: 'mdi:soccer',
      soccer: 'mdi:soccer',
      volley: 'mdi:volleyball',
      volleyball: 'mdi:volleyball',
      badminton: 'mdi:badminton',
      basketball: 'mdi:basketball',
      tennis: 'mdi:tennis',
    };
    return icons[sportType.toLowerCase()] || 'mdi:soccer';
  };

  // Normalize linkGroup to ensure it is an absolute URL.
  const normalizedLinkGroup = event.linkGroup
    ? /^https?:\/\//i.test(event.linkGroup)
      ? event.linkGroup
      : `https://${event.linkGroup}`
    : undefined;

  return (
    <div
      onClick={() => onViewDetail?.(event)}
      className="bg-white rounded-2xl p-4 relative shadow-[inset_0_0_0_1px_#E0E6ED] border-none cursor-pointer hover:shadow-xs transition-shadow"
    >
      {/* Header: Badges and Menu */}
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center gap-2 flex-wrap">
          {/* Host Badge - only show if user is host */}
          {event.isHost && (
            <span className="px-2.5 py-0.5 rounded-full text-xs font-semibold bg-[#FFF2EE] text-[#FC480D] shadow-[inset_0_0_0_1px_#FC480D]">
              Host
            </span>
          )}
          {/* Private Badge */}
          {event.isPrivate && (
            <span className="px-2.5 py-0.5 rounded-full text-xs font-semibold bg-gray-100 text-gray-600">
              Private
            </span>
          )}
          {/* Sport Type Badge */}
          <span className="inline-flex items-center gap-1.5 bg-[#FFF2EE] text-[#FC480D] px-2.5 py-1 rounded-full text-xs font-semibold">
            <Icon icon={event.sportIcon || getSportIcon(event.sportType)} className="w-3.5 h-3.5" />
            {event.sportType}
          </span>
          {/* Participant Status Badge */}
          {event.isParticipant && (event.participantStatus || isUnpaidQris) && (
            <span
              className={`px-2.5 py-0.5 rounded-full text-xs font-semibold ${
                event.participantStatus === 'waiting_leave'
                  ? 'bg-amber-50 text-amber-600'
                  : event.participantStatus === 'waiting_list'
                    ? 'bg-amber-50 text-amber-700 border border-amber-200/70'
                    : event.participantStatus === 'unpaid' || isUnpaidQris
                      ? 'bg-amber-50 text-amber-700'
                      : event.participantStatus === 'confirmed'
                        ? 'bg-emerald-50 text-emerald-600'
                        : 'bg-yellow-50 text-yellow-700'
              }`}
            >
              {event.participantStatus === 'waiting_leave'
                ? 'Menunggu Approval Leave'
                : event.participantStatus === 'waiting_list'
                  ? 'Waiting List'
                  : event.participantStatus === 'unpaid' || isUnpaidQris
                    ? 'Menunggu Pembayaran'
                    : event.participantStatus === 'confirmed'
                      ? 'Terkonfirmasi'
                      : 'Pending Review'}
            </span>
          )}
        </div>
      </div>

      {/* Event Title */}
      <h3 className="text-base font-semibold text-[#0E1726] mb-3 leading-snug">{event.title}</h3>

      {/* Event Details */}
      <div className="space-y-2 mb-4">
        <div className="flex items-center gap-2 text-xs text-[#556575]">
          <Icon icon="ph:calendar-blank" className="w-4 h-4 text-gray-400 shrink-0" />
          <span>{event.date}</span>
        </div>
        <div className="flex items-center gap-2 text-xs text-[#556575]">
          <Icon icon="ph:clock" className="w-4 h-4 text-gray-400 shrink-0" />
          <span>{event.time}</span>
        </div>
        <div className="flex items-center gap-2 text-xs text-[#556575]">
          <Icon icon="ph:map-pin" className="w-4 h-4 text-gray-400 shrink-0" />
          <span className="truncate">{event.venue}</span>
        </div>
        <div className="flex items-center gap-2 text-xs text-[#556575]">
          <Icon icon="ph:users-three" className="w-4 h-4 text-[#FC480D] shrink-0" />
          <span className="font-medium text-[#0E1726]">
            {event.currentParticipants}/{event.maxParticipants} Peserta ({participantPercentage}%)
          </span>
        </div>
      </div>

      {/* Action Button */}
      {event.isHost ? (
        <Link
          to={`/event/${event.slug}`}
          className="w-full h-11 rounded-full bg-[#FC480D] hover:bg-[#D83B08] text-white font-semibold transition-colors text-center flex items-center justify-center gap-2 text-sm cursor-pointer shadow-xs"
        >
          Lihat Peserta
        </Link>
      ) : event.isParticipant ? (
        <div className="flex flex-col gap-2">
          {isUnpaidQris ? (
            <button
              onClick={(e) => {
                e.stopPropagation();
                onPay?.(event);
              }}
              className="w-full h-11 rounded-full bg-[#FC480D] text-white font-semibold hover:bg-[#D83B08] transition-colors text-center flex items-center justify-center gap-2 cursor-pointer shadow-xs text-sm"
            >
              <Icon icon="ph:credit-card" className="w-4 h-4" />
              Bayar Sekarang
            </button>
          ) : (
            <Link
              to={`/event/${event.slug}`}
              onClick={(e) => e.stopPropagation()}
              className="w-full h-11 rounded-full bg-[#FFF2EE] text-[#FC480D] font-semibold hover:bg-[#FFE6DD] transition-colors text-center flex items-center justify-center gap-2 text-sm cursor-pointer"
            >
              <Icon icon="ph:info" className="w-4 h-4" />
              Lihat Detail Event
            </Link>
          )}

          {normalizedLinkGroup ? (
            <div className="grid grid-cols-2 gap-2">
              <button
                disabled={event.participantStatus === 'waiting_leave'}
                onClick={(e) => {
                  e.stopPropagation();
                  onLeave?.(event);
                }}
                className={`w-full h-11 rounded-full bg-red-50 text-red-600 font-semibold text-xs flex items-center justify-center gap-1.5 ${event.participantStatus === 'waiting_leave' ? 'cursor-not-allowed opacity-60' : 'hover:bg-red-100 transition-colors cursor-pointer'}`}
              >
                {event.participantStatus === 'waiting_leave' ? (
                  <>
                    <Icon icon="ph:clock" className="w-4 h-4" />
                    Proses Review
                  </>
                ) : (
                  <>
                    <Icon icon="ph:sign-out" className="w-4 h-4" />
                    Leave Event
                  </>
                )}
              </button>

              <button
                type="button"
                onClick={(e) => {
                  e.stopPropagation();
                  void openExternalUrl(normalizedLinkGroup);
                }}
                className="w-full h-11 rounded-full bg-[#FFF2EE] text-[#FC480D] font-semibold text-xs flex items-center justify-center gap-1.5 hover:bg-[#FFE6DD] transition-colors"
              >
                <Icon icon="ph:users-three" className="w-4 h-4" />
                Join Group
              </button>
            </div>
          ) : (
            <button
              disabled={event.participantStatus === 'waiting_leave'}
              onClick={(e) => {
                e.stopPropagation();
                onLeave?.(event);
              }}
              className={`w-full h-11 rounded-full bg-red-50 text-red-600 font-semibold text-xs flex items-center justify-center gap-1.5 ${event.participantStatus === 'waiting_leave' ? 'cursor-not-allowed opacity-60' : 'hover:bg-red-100 transition-colors cursor-pointer'}`}
            >
              {event.participantStatus === 'waiting_leave' ? (
                <>
                  <Icon icon="ph:clock" className="w-4 h-4" />
                  Proses Review
                </>
              ) : (
                <>
                  <Icon icon="ph:sign-out" className="w-4 h-4" />
                  Leave Event
                </>
              )}
            </button>
          )}
        </div>
      ) : (
        <button
          onClick={(e) => {
            e.stopPropagation();
            onJoin?.(event);
          }}
          className="w-full h-11 rounded-full bg-[#FC480D] text-white font-semibold hover:bg-[#D83B08] transition-colors flex items-center justify-center gap-2 text-sm cursor-pointer shadow-xs"
        >
          Join Group
        </button>
      )}

      {/* Padel / Badminton management CTA - privileged customers only */}
      {showPadelCta && event.sportSlug === 'padel' && (
        <button
          onClick={(e) => {
            e.stopPropagation();
            onViewPadel?.(event);
          }}
          className="mt-2 w-full h-11 rounded-full border border-[#FC480D] text-[#FC480D] font-semibold hover:bg-[#FFF2EE] transition-colors flex items-center justify-center gap-2 text-xs cursor-pointer"
        >
          <Icon icon="ph:chart-line-up" className="w-4 h-4" />
          Statistik Padel
        </button>
      )}
      {showBadmintonCta && event.sportSlug === 'badminton' && (
        <button
          onClick={(e) => {
            e.stopPropagation();
            onViewBadminton?.(event);
          }}
          className="mt-2 w-full h-11 rounded-full border border-[#FC480D] text-[#FC480D] font-semibold hover:bg-[#FFF2EE] transition-colors flex items-center justify-center gap-2 text-xs cursor-pointer"
        >
          <Icon icon="ph:chart-line-up" className="w-4 h-4" />
          Statistik Badminton
        </button>
      )}
    </div>
  );
};

export default MabarClientCard;
